import React, { useState, useEffect, useRef } from 'react';
import {
    Layout,
    Card,
    Form,
    Input,
    Button,
    Typography,
    message,
    Result,
    Space,
    Grid,
    Row,
    Col
} from 'antd';
import {
    BankOutlined,
    ArrowRightOutlined,
    CheckCircleOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import fayda from "./assets/img_2.png";
import Logo from "./assets/img_1.png";
import Nid from "./assets/img.png";
import { ApiService } from "./ApiService.jsx";
import OtpVerificationStep from "./OtpVerification.jsx";

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const IdInputStep = ({ onSubmit, loading, canvasRef, onRefreshCaptcha }) => (
    <Card
        bordered={false}
        style={{ boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', borderRadius: '8px' }}
    >
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <Title level={3} style={{ marginBottom: '0.5rem', color: '#1f2937' }}>Account Verification</Title>
            <Text type="secondary">Please enter your account number to proceed.</Text>
        </div>
        <Form
            layout="vertical"
            onFinish={onSubmit}
            requiredMark={false}
            size="large"
        >
            <Form.Item
                name="account"
                label={<span style={{ fontWeight: 500, color: '#374151' }}>Account Number</span>}
                rules={[
                    { required: true, message: 'Please enter your account number' },
                    {
                        pattern: /^\d{13}$/,
                        message: 'Account number must be exactly 13 digits'
                    }
                ]}
            >
                <Input
                    prefix={<BankOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder="e.g. 100000000001"
                    maxLength={13}
                    style={{ padding: '0.625rem 0.75rem' }}
                />
            </Form.Item>

            <Form.Item label={<span style={{ fontWeight: 500, color: '#374151' }}>Verification CAPTCHA</span>} required>
                <Row gutter={12} align="middle">
                    <Col span={12}>
                        <Form.Item
                            name="captcha"
                            noStyle
                            rules={[{ required: true, message: 'Please enter the code shown' }]}
                        >
                            <Input
                                placeholder="Enter code"
                                style={{ height: '40px' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: '#fff',
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            padding: '2px 4px',
                            height: '40px'
                        }}>
                            <canvas
                                ref={canvasRef}
                                width="140"
                                height="34"
                                style={{ cursor: 'pointer', flex: 1 }}
                                onClick={onRefreshCaptcha}
                            />
                            <Button
                                type="text"
                                size="small"
                                icon={<ReloadOutlined style={{ color: '#8c8c8c' }} />}
                                onClick={onRefreshCaptcha}
                            />
                        </div>
                    </Col>
                </Row>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
                <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                    size="large"
                    style={{ height: '2.75rem', fontWeight: 500 }}
                    icon={<ArrowRightOutlined />}
                    iconPosition="end"
                    disabled={loading || !canvasRef}
                >
                    Request Verification Code
                </Button>
            </Form.Item>
        </Form>
    </Card>
);

const SuccessStep = () => (
    <Card
        bordered={false}
        style={{ boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', textAlign: 'center', padding: '2rem 0', borderRadius: '8px' }}
    >
        <Result
            status="success"
            icon={<CheckCircleOutlined style={{ fontSize: '5rem', color: '#10b981' }} />}
            title="Thank You! Verification Complete"
            subTitle={
                <Space direction="vertical" size="small" style={{ marginTop: '1rem' }}>
                    <p style={{ fontSize: '1rem', color: '#374151', marginBottom: '0.5rem' }}>
                        We appreciate you taking the time to verify your identity.
                    </p>
                    <p style={{ fontWeight: 600, color: '#10b981', marginTop: '1rem', fontSize: '0.95rem' }}>
                        You're all set! Welcome aboard.
                    </p>
                </Space>
            }
            extra={[
                <Button type="primary" key="console" size="large" style={{ backgroundColor: '#10b981', borderColor: '#10b981', fontWeight: 500 }}>
                    Continue to Dashboard
                </Button>,
                <Button key="close" size="large">Close</Button>
            ]}
        />
    </Card>
);

const UserPortal = ({ onSwitchToAdmin }) => {
    const screens = useBreakpoint();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lastDigit, setLastDigit] = useState("");
    const [account, setAccount] = useState("");
    const [captchaText, setCaptchaText] = useState("");
    const canvasRef = useRef(null);
    const [messageApi, contextHolder] = message.useMessage();

    const generateCaptcha = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let result = '';
        for (let i = 0; i < 6; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setCaptchaText(result);
        drawCaptcha(result);
    };

    const drawCaptcha = (text) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        // Background styling
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#fdfdfd';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add some noise dots in the background
        for (let i = 0; i < 50; i++) {
            ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.2)`;
            ctx.beginPath();
            ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, 1, 0, Math.PI * 2);
            ctx.fill();
        }

        // Draw random interference lines
        for (let i = 0; i < 4; i++) {
            ctx.strokeStyle = `rgba(${Math.random() * 255}, 0, 0, 0.2)`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
            ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
            ctx.stroke();
        }

        // Text styling
        ctx.textBaseline = 'middle';
        const colors = ['#1677ff', '#cf1322', '#389e0d', '#d46b08', '#000000'];

        for (let i = 0; i < text.length; i++) {
            const x = 15 + i * 20;
            const y = 20 + (Math.random() - 0.5) * 10;
            const angle = (Math.random() - 0.5) * 0.4;

            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(angle);
            ctx.font = `bold ${20 + Math.random() * 4}px 'Courier New'`;
            ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
            ctx.fillText(text[i], 0, 0);
            ctx.restore();
        }

        // Add a strike-through line across the whole text
        ctx.strokeStyle = 'rgba(0,0,0,0.3)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(10, 20 + (Math.random() - 0.5) * 10);
        ctx.lineTo(canvas.width - 10, 20 + (Math.random() - 0.5) * 10);
        ctx.stroke();
    };

    useEffect(() => {
        if (currentStep === 0) {
            generateCaptcha();
        }
    }, [currentStep]);

    const handleIdSubmit = (values) => {
        if (values.captcha.toUpperCase() !== captchaText) {
            messageApi.error('Invalid CAPTCHA code');
            generateCaptcha();
            return;
        }
        setAccount(values.account);
        fetchData(values.account);
    };

    const fetchData = async (accountNumber) => {
        setLoading(true);
        const res = await ApiService.sendOtp(accountNumber);
        if (res.status === true) {
            setLastDigit(res.ob.phoneNumber);
            setLoading(false);
            messageApi.success('Verification code sent');
            setCurrentStep(1);
        } else {
            setLoading(false);
            messageApi.error(res.message);
            generateCaptcha();
        }
    };

    const handleResendOtp = async () => {
        const res = await ApiService.sendOtp(account);
        if (res.status === true) {
            return true;
        } else {
            messageApi.error(res.message);
            return false;
        }
    };

    const handleOtpSubmit = (values) => {
        getAuthorizationUrl(values.otp);
    };

    const getAuthorizationUrl = async (otpValue) => {
        try {
            setLoading(true);
            const response = await ApiService.fetchAuthorizationUrl({
                otp: otpValue,
                channel: "web",
                account: account
            });

            if (response.status === true) {
                window.location.replace(response.ob.url);
            } else {
                messageApi.error(response.message);
            }
        } catch (error) {
            messageApi.error(error.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <IdInputStep
                        onSubmit={handleIdSubmit}
                        loading={loading}
                        canvasRef={canvasRef}
                        onRefreshCaptcha={generateCaptcha}
                    />
                );
            case 1:
                return (
                    <OtpVerificationStep
                        onSubmit={handleOtpSubmit}
                        loading={loading}
                        lastDigit={lastDigit}
                        onResend={handleResendOtp}
                    />
                );
            case 3:
                return <SuccessStep />;
            default:
                return <IdInputStep onSubmit={handleIdSubmit} loading={loading} canvasRef={canvasRef} onRefreshCaptcha={generateCaptcha} />;
        }
    };

    return (
        <>
            {contextHolder}
            <Layout style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
                {currentStep !== 2 && (
                    <Header style={{
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #f3f4f6',
                        padding: '0 24px',
                        height: 64,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                    }}>
                        <Space style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={Logo} alt="Logo" style={{ width: '100px', height: '40px', objectFit: 'contain' }} />
                            <Text strong style={{ fontSize: '1.125rem', color: '#1f2937' }}>with</Text>
                            <img src={fayda} alt="Logo" style={{ width: '100px', height: '40px', objectFit: 'contain' }} />
                        </Space>
                        {screens.md && (
                            <Space style={{ display: 'flex', alignItems: 'center' }}>
                                <img src={Nid} alt="Logo" style={{ width: '100px', height: '40px', objectFit: 'contain' }} />
                                <Text type="secondary" style={{ fontSize: '0.875rem' }}>National Id Harmonization portal</Text>
                            </Space>
                        )}
                    </Header>
                )}

                <Content style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem 1.5rem',
                }}>
                    <div style={{ width: '100%', maxWidth: '448px' }}>
                        {renderStep()}
                    </div>
                </Content>

                {currentStep !== 2 && (
                    <Footer style={{
                        textAlign: 'center',
                        color: '#9ca3af',
                        backgroundColor: '#f9fafb',
                        padding: '1.5rem 0',
                        fontSize: '0.75rem',
                    }}>
                        © 2025 Hijra Bank S.C. All rights reserved. Privacy Policy | Terms of Service
                    </Footer>
                )}
            </Layout>
        </>
    );
};

export default UserPortal;